import { getChronicleDeviceProps } from '@/app-shell/chronicle/analytics/chronicle-device-props';
import { createEnrichedLogger } from '@/utils/logger/create-enriched-logger';
import { type CustomLogger } from '@/utils/logger/types';

/**
 * APP-6285: merge a live Heidi Remote device-state snapshot under
 * `heidi_remote_device` onto Chronicle log payloads. This scopes the device
 * context to Chronicle / Heidi Remote logs only (mirroring how `dictationLogger`
 * scopes correlation ids), rather than a global Datadog context that would tag
 * every log line app-wide.
 *
 * Only object payloads are enriched: Error payloads are left untouched so
 * Datadog's stack-trace extraction isn't broken, and string payloads are
 * wrapped into `{ msg }` by the caller (the Rust-log forwarder) before logging
 * so the snapshot can attach. The snapshot is empty (and so a no-op) when no
 * device has been seen, so the absence of `heidi_remote_device` means
 * "no Remote connected".
 */
const enrich = (payload: unknown): unknown => {
  const device = getChronicleDeviceProps();
  if (Object.keys(device).length === 0) return payload;
  if (payload !== null && typeof payload === 'object' && !(payload instanceof Error)) {
    return { heidi_remote_device: device, ...(payload as Record<string, unknown>) };
  }
  return payload;
};

/**
 * Module-scoped logger for Chronicle / Heidi Remote. Tags logs with
 * `module: 'chronicle'` and auto-merges the live device snapshot
 * (`heidi_remote_device.*`) on every call, so device identity / signal can be
 * correlated with BLE operations without per-call boilerplate. Child loggers
 * inherit the enrichment.
 */
export const chronicleLogger: CustomLogger = createEnrichedLogger('chronicle', enrich);
